import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Building2, Save, X, Loader2 } from 'lucide-react';
import { Button, ConfirmDialog } from '../components/ui';
import { HotelCard } from '../components/HotelCard';
import { EditHotelForm } from '../components/EditHotelForm';
import { HotelWithProgress } from '../types';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { hotelNameToSlug } from '../utils/format';
import { useNavigate, useLocation } from 'react-router-dom';
import { logger } from '../utils/logger';

export function HotelOverview() {
  const { isAdmin, isManagement, isHotelStaff, assignedHotelIds } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [hotels, setHotels] = useState<HotelWithProgress[]>([]);
  const [filteredHotels, setFilteredHotels] = useState<HotelWithProgress[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddHotel, setShowAddHotel] = useState(false);
  const [editingHotel, setEditingHotel] = useState<HotelWithProgress | null>(null);
  const [deletingHotel, setDeletingHotel] = useState<HotelWithProgress | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(null);
  
  // Refs for timeout cleanup
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadHotels();
  }, []);

  // Set up real-time listener for hotels
  useEffect(() => {
    const unsubscribe = apiService.subscribeToHotels((updatedHotels) => {
      setHotels(updatedHotels);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  // Auto-redirect users based on their role and assignments
  useEffect(() => {
    if (hotels.length > 0) {
      if (isAdmin || isManagement) {
        navigate('/dashboard');
      } else if (isHotelStaff) {
        if (assignedHotelIds.length === 0) {
          // Explicitly route to no-access page
          if (location.pathname !== '/no-access') navigate('/no-access');
          return;
        }
        if (assignedHotelIds.length === 1) {
          const assignedHotel = hotels.find(hotel => hotel.id === assignedHotelIds[0]);
          if (assignedHotel) navigate(`/hotel/${hotelNameToSlug(assignedHotel.name)}`);
        } else if (assignedHotelIds.length > 1) {
          navigate('/hotels');
        }
      }
    }
  }, [isAdmin, isManagement, isHotelStaff, assignedHotelIds, hotels, navigate, location.pathname]);

  useEffect(() => {
    filterHotels();
  }, [hotels, searchQuery, assignedHotelIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close edit modal if the hotel being edited is no longer in the hotels list
  useEffect(() => {
    if (editingHotel && !hotels.find(hotel => hotel.id === editingHotel.id)) {
      setEditingHotel(null);
    }
  }, [hotels, editingHotel]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const loadHotels = async () => {
    try {
      setLoading(true);
      const data = await apiService.getHotels();
      setHotels(data);
      setError(null);
    } catch (err) {
      setError('Failed to load hotels');
      logger.error('Error loading hotels:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterHotels = () => {
    logger.log('🔍 Filtering hotels:', { isHotelStaff, assignedHotelIds, totalHotels: hotels.length });
    
    // First filter by user access permissions
    let accessibleHotels = hotels;
    if (isHotelStaff) {
      // Hotel staff can only see their assigned hotels
      accessibleHotels = hotels.filter(hotel => assignedHotelIds.includes(hotel.id));
      logger.log('🏨 Hotel staff filtering:', { 
        totalHotels: hotels.length, 
        assignedHotels: accessibleHotels.length, 
        assignedHotelIds 
      });
    }
    // Admins and Management can see all hotels (no filtering needed)

    // Apply search filter
    let filtered = accessibleHotels;
    if (searchQuery.trim()) {
      filtered = accessibleHotels.filter(hotel =>
        hotel.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        hotel.contactPerson.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort hotels for admins and management: in-progress first, then no progress, then completed
    if (isAdmin || isManagement) {
      filtered = [...filtered].sort((a, b) => {
        // Hotels in progress (progress > 0% and < 100%)
        const aInProgress = a.progressPercentage > 0 && a.progressPercentage < 100;
        const bInProgress = b.progressPercentage > 0 && b.progressPercentage < 100;
        
        // Hotels with no progress (progress = 0%)
        const aNoProgress = a.progressPercentage === 0;
        const bNoProgress = b.progressPercentage === 0;
        
        // Hotels completed (progress = 100%) - not used in sorting logic but kept for clarity

        // Sort order: in-progress first, then no progress, then completed
        if (aInProgress && !bInProgress) return -1;
        if (!aInProgress && bInProgress) return 1;
        if (aNoProgress && !bNoProgress && !aInProgress && !bInProgress) return -1;
        if (!aNoProgress && bNoProgress && !aInProgress && !bInProgress) return 1;
        
        // Within same category, sort by name alphabetically
        return a.name.localeCompare(b.name);
      });
    }

    setFilteredHotels(filtered);
    logger.log('✅ Final filtered and sorted hotels:', filtered.length);
  };

  const handleAddHotel = async (hotelData: {
    name: string;
    contactPerson: string;
    imageUrl: string;
    startDate: string;
  }) => {
    try {
      const newHotel = await apiService.createHotel({
        ...hotelData,
        startDate: hotelData.startDate, // Use the provided start date
        createdAt: new Date().toISOString(),
        isActive: true,
        createdBy: 'admin-1', // In real app, get from auth
        settings: {
          timezone: 'Europe/Stockholm',
          language: 'en'
        }
      });
      
      // Apply templates to the new hotel
      try {
        await apiService.applyTemplatesToHotel(newHotel.id);
        logger.log('✅ Templates applied successfully to new hotel');
        
        // Add a small delay to ensure Firestore consistency
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setShowSuccessMessage(`Hotel "${newHotel.name}" created successfully with template tasks!`);
        
        // Clear any existing timeout and set new one
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => setShowSuccessMessage(null), 5000);
      } catch (templateErr) {
        logger.error('❌ Error applying templates:', templateErr);
        setError(`Hotel "${newHotel.name}" created, but there was an issue applying template tasks. Please check the console for details.`);
        
        // Clear any existing timeout and set new one
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
      }
      
      // Don't manually add to state - let the real-time listener handle it
      setShowAddHotel(false);
    } catch (err) {
      logger.error('Error creating hotel:', err);
      setError('Failed to create hotel');
      
      // Clear any existing timeout and set new one
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
    }
  };

  const handleEditHotel = async (hotelData: {
    name: string;
    contactPerson: string;
    imageUrl: string;
    startDate: string;
  }) => {
    if (!editingHotel) return;
    
    try {
      await apiService.updateHotel(editingHotel.id, hotelData);
      // Don't manually update state - let the real-time listener handle it
      setEditingHotel(null);
      
      // Show success message
      setShowSuccessMessage(`Hotel "${hotelData.name}" updated successfully!`);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setShowSuccessMessage(null), 5000);
    } catch (err) {
      logger.error('Error updating hotel:', err);
      setError('Failed to update hotel');
      
      // Clear any existing timeout and set new one
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeleteHotel = async (hotel: HotelWithProgress) => {
    setDeletingHotel(hotel);
  };

  const confirmDeleteHotel = async () => {
    if (!deletingHotel) return;
    
    try {
      const hotelName = deletingHotel.name;
      await apiService.deleteHotel(deletingHotel.id);
      // Don't manually update state - let the real-time listener handle it
      setDeletingHotel(null);
      
      // Close edit modal if the deleted hotel was being edited
      if (editingHotel && editingHotel.id === deletingHotel.id) {
        setEditingHotel(null);
      }
      
      // Show success message
      setShowSuccessMessage(`Hotel "${hotelName}" deleted successfully!`);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setShowSuccessMessage(null), 5000);
    } catch (err) {
      logger.error('Error deleting hotel:', err);
      setError('Failed to delete hotel');
      
      // Clear any existing timeout and set new one
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 5000);
    }
  };

  const calculateStats = () => {
    const totalHotels = hotels.length;
    const completedHotels = hotels.filter(h => h.progressPercentage === 100).length;
    const averageProgress = hotels.length > 0 
      ? Math.round(hotels.reduce((sum, h) => sum + h.progressPercentage, 0) / hotels.length)
      : 0;
    const inProgressHotels = hotels.filter(h => h.progressPercentage > 0 && h.progressPercentage < 100).length;

    return {
      totalHotels,
      completedHotels,
      averageProgress,
      inProgressHotels
    };
  };

  const stats = calculateStats();

  // Group hotels by progress status for admin and management view
  const groupedHotels = (isAdmin || isManagement) ? (() => {
    const inProgress = filteredHotels.filter(hotel => 
      hotel.progressPercentage > 0 && hotel.progressPercentage < 100
    );
    const notStarted = filteredHotels.filter(hotel => 
      hotel.progressPercentage === 0
    );
    const completed = filteredHotels.filter(hotel => 
      hotel.progressPercentage === 100
    );
    
    return { inProgress, notStarted, completed };
  })() : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger-600 mb-4">{error}</p>
          <Button onClick={loadHotels}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center justify-between">
          <div className="space-y-3">
            <h1 className="text-5xl font-display font-bold text-gray-900 tracking-tight">
              {isHotelStaff ? 'My Hotels' : 'Dashboard'}
            </h1>
            <p className="text-gray-600 text-lg font-text font-medium leading-relaxed max-w-2xl">
              {isHotelStaff 
                ? assignedHotelIds.length === 1
                  ? `Track Scout AI deployment for ${hotels.find(h => h.id === assignedHotelIds[0])?.name || 'your hotel'}`
                  : `Track Scout AI deployment for ${assignedHotelIds.length} hotels`
                : 'Track Scout AI deployment across all hotels'
              }
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Primary CTA - Only show for admins */}
            {isAdmin && (
              <Button 
                variant="primary" 
                size="lg" 
                onClick={() => setShowAddHotel(true)}
                className="shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30"
              >
                <Plus className="w-5 h-5 mr-3" />
                <span className="font-bold">Add New Hotel</span>
              </Button>
            )}
            

          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl">
          <p className="text-green-700">{showSuccessMessage}</p>
        </div>
      )}

      {/* Stats Cards - Only show for admins and management */}
      {(isAdmin || isManagement) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {/* Total Hotels */}
          <div className="bg-white/30 backdrop-blur-xl p-8 rounded-2xl border border-gray-200 shadow-lg hover:bg-white/40 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group relative">
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-xs font-text font-semibold text-neutral-700 uppercase tracking-wider">Total Hotels</p>
                <p className="text-5xl font-display font-bold text-deepAccent-500 leading-none">{stats.totalHotels}</p>
              </div>
              <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-deepAccent-400 to-deepAccent-500 rounded-full transition-all duration-500" style={{width: '100%'}}></div>
              </div>
            </div>
          </div>

          {/* Completed */}
          <div className="bg-white/30 backdrop-blur-xl p-8 rounded-2xl border border-gray-200 shadow-lg hover:bg-white/40 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group relative">
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-xs font-text font-semibold text-neutral-700 uppercase tracking-wider">Completed</p>
                <p className="text-5xl font-display font-bold text-primary-500 leading-none">{stats.completedHotels}</p>
              </div>
              <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary-400 to-primary-500 rounded-full transition-all duration-500" style={{width: stats.totalHotels > 0 ? `${(stats.completedHotels / stats.totalHotels) * 100}%` : '0%'}}></div>
              </div>
            </div>
          </div>

          {/* In Progress */}
          <div className="bg-white/30 backdrop-blur-xl p-8 rounded-2xl border border-gray-200 shadow-lg hover:bg-white/40 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 group relative">
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-xs font-text font-semibold text-neutral-700 uppercase tracking-wider">In Progress</p>
                <p className="text-5xl font-display font-bold text-accent-500 leading-none">{stats.inProgressHotels}</p>
              </div>
              <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-accent-400 to-accent-500 rounded-full transition-all duration-500" style={{width: stats.totalHotels > 0 ? `${(stats.inProgressHotels / stats.totalHotels) * 100}%` : '0%'}}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Section - Enhanced typography contrast */}
      <div className="mb-12">
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-display font-bold text-gray-900">Find Hotels</h2>
            <p className="text-gray-600 font-text">Search and filter your Scout AI deployment properties</p>
          </div>
          
          <div className="relative max-w-2xl">
            <div className="absolute left-6 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-white/50 backdrop-blur-sm rounded-full flex items-center justify-center z-10">
              <Search className="w-4 h-4 text-gray-700" />
            </div>
            <input
              type="text"
              placeholder="Search by hotel name, location, or contact person..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-16 pr-8 py-5 text-lg bg-white/25 backdrop-blur-2xl border border-gray-200 rounded-3xl focus:outline-none focus:ring-4 focus:ring-primary-500/30 focus:border-primary-400/50 hover:shadow-lg hover:bg-white/35 transition-all duration-300 font-text placeholder-gray-500 shadow-md"
            />
          </div>
          
          {searchQuery && (
            <div className="bg-white/20 backdrop-blur-xl px-6 py-3 rounded-2xl border border-gray-200 inline-block">
              <p className="text-sm text-gray-700 font-text font-semibold">
                Found {filteredHotels.length} of {hotels.length} hotels
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Hotels Section */}
      <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-gray-200 shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
        <div className="p-10 border-b border-white/20 relative z-10">
          <div className="space-y-4">
            <h2 className="text-4xl font-display font-bold text-gray-900 tracking-tight">Your Hotels</h2>
            <p className="text-lg text-gray-600 font-text font-medium leading-relaxed max-w-3xl">
              Manage and monitor project progress across all your properties. Track progress, view details, and ensure successful deployments.
            </p>
          </div>
        </div>
        
        <div className="p-10">
          {filteredHotels.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-3xl font-display font-bold text-gray-700 mb-4">
                {isHotelStaff && assignedHotelIds.length === 0 
                  ? 'No Hotels Assigned' 
                  : searchQuery 
                    ? 'No Hotels Found' 
                    : 'No Hotels Yet'
                }
              </h3>
              <p className="text-lg text-gray-500 font-text max-w-md mx-auto leading-relaxed mb-8">
                {isHotelStaff && assignedHotelIds.length === 0
                  ? 'You don\'t have access to any hotels. Please contact your administrator to get assigned to hotels.'
                  : searchQuery 
                    ? 'Try adjusting your search terms or browse all hotels.' 
                    : 'Get started by adding your first hotel to track deployment progress.'
                }
              </p>
              {!searchQuery && isAdmin && (
                <Button variant="primary" size="lg">
                  <Plus className="w-5 h-5 mr-3" />
                  <span className="font-bold">Add Your First Hotel</span>
                </Button>
              )}
            </div>
          ) : (isAdmin || isManagement) && groupedHotels ? (
            // Admin view with grouped sections
            <div className="space-y-12">
              {/* In Progress Section */}
              {groupedHotels.inProgress.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                    <h2 className="text-2xl font-display font-bold text-gray-900">
                      In Progress ({groupedHotels.inProgress.length})
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 400px))', maxWidth: '100%', justifyContent: 'start' }}>
                    {groupedHotels.inProgress.map((hotel) => (
                      <HotelCard 
                        key={hotel.id} 
                        hotel={hotel} 
                        isAdmin={isAdmin}
                        onEditHotel={isAdmin ? setEditingHotel : undefined}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Not Started Section */}
              {groupedHotels.notStarted.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <h2 className="text-2xl font-display font-bold text-gray-900">
                      Not Started ({groupedHotels.notStarted.length})
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 400px))', maxWidth: '100%', justifyContent: 'start' }}>
                    {groupedHotels.notStarted.map((hotel) => (
                      <HotelCard 
                        key={hotel.id} 
                        hotel={hotel} 
                        isAdmin={isAdmin}
                        onEditHotel={isAdmin ? setEditingHotel : undefined}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Completed Section */}
              {groupedHotels.completed.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <h2 className="text-2xl font-display font-bold text-gray-900">
                      Completed ({groupedHotels.completed.length})
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 400px))', maxWidth: '100%', justifyContent: 'start' }}>
                    {groupedHotels.completed.map((hotel) => (
                      <HotelCard 
                        key={hotel.id} 
                        hotel={hotel} 
                        isAdmin={isAdmin}
                        onEditHotel={isAdmin ? setEditingHotel : undefined}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Regular view for hotel staff or when no grouping
            <div className="grid grid-cols-1 gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 400px))', maxWidth: '100%', justifyContent: 'start' }}>
              {filteredHotels.map((hotel) => (
                <HotelCard 
                  key={hotel.id} 
                  hotel={hotel} 
                  isAdmin={isAdmin}
                  onEditHotel={isAdmin ? setEditingHotel : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Hotel Modal */}
      {showAddHotel && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold text-neutral-900">
                Add New Hotel
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddHotel(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <AddHotelForm
              onSave={handleAddHotel}
              onCancel={() => setShowAddHotel(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Hotel Modal */}
      {editingHotel && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold text-neutral-900">
                Edit Hotel
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingHotel(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <EditHotelForm
              hotel={editingHotel}
              onSave={handleEditHotel}
              onCancel={() => setEditingHotel(null)}
              onDelete={isAdmin ? () => handleDeleteHotel(editingHotel) : undefined}
            />
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={!!deletingHotel}
        onClose={() => setDeletingHotel(null)}
        onConfirm={confirmDeleteHotel}
        title="Delete Hotel"
        message={`Are you sure you want to delete "${deletingHotel?.name}"? This action cannot be undone and will remove all associated tasks and data.`}
        variant="danger"
        confirmText="Delete Hotel"
      />
    </div>
  );
}

// Add Hotel Form Component
interface AddHotelFormProps {
  onSave: (hotelData: { name: string; contactPerson: string; imageUrl: string; startDate: string }) => void;
  onCancel: () => void;
}

function AddHotelForm({ onSave, onCancel }: AddHotelFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    imageUrl: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    startDate: new Date().toISOString().split('T')[0], // Default to today
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Hotel Name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          placeholder="Enter hotel name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Contact Person
        </label>
        <input
          type="text"
          value={formData.contactPerson}
          onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          placeholder="Enter contact person name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Start Date
        </label>
        <input
          type="date"
          value={formData.startDate}
          onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Hotel Image URL
        </label>
        <div className="space-y-3">
          <input
            type="url"
            value={formData.imageUrl}
            onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
            placeholder="Enter image URL"
            required
          />
          {formData.imageUrl && (
            <div className="relative">
              <img
                src={formData.imageUrl}
                alt="Hotel preview"
                className="w-full h-48 object-cover rounded-2xl border border-gray-200"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                }}
              />
              <div className="absolute inset-0 bg-black/20 rounded-2xl flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200">
                <div className="bg-white/90 backdrop-blur-sm px-3 py-2 rounded-xl">
                  <span className="text-sm font-text font-semibold text-gray-700">Image Preview</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end space-x-4 pt-6">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          <Save className="w-4 h-4 mr-2" />
          Create Hotel
        </Button>
      </div>
    </form>
  );
}
